import store from '@/store'
import { getDatabase, ref, onValue, get } from 'firebase/database'
import { getFirebaseApp } from './firebase'

// 強制登出功能開關
// 暫留 false，staging 驗證 onValue timeout / get 備援 / fail-open 後再開 true
const FORCE_LOGOUT_ENABLED = true

// onValue 取初始值的逾時（慢速網路 3G 約需 3 秒，故設 5 秒）
const ONVALUE_TIMEOUT_MS = 5000
// get() 備援的逾時
const GET_TIMEOUT_MS = 5000

// 儲存當前的監聽器取消函數
let currentUnsubscribeFunction = null
// 儲存當前的 onValue 逾時計時器，切換社區時需一併清除避免舊計時器誤觸
let currentTimeoutId = null

/**
 * 將 promise 包上逾時，逾時則 reject
 * @param {Promise} promise - 目標 promise
 * @param {number} ms - 逾時毫秒數
 * @returns {Promise}
 */
function withTimeout (promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), ms))
  ])
}

/**
 * 處理資料變更
 * @param {object} snapshot - Firebase 資料快照
 */
function handleDataChange (snapshot) {
  if (!snapshot.exists()) {
    // 未被強制登出
    store.commit('triggerForceLogout', false)
    return
  }

  const data = snapshot.val()
  const loginUserInfo = JSON.parse(localStorage.getItem('loginUserInfo') || '{}')
  const { Account: account } = loginUserInfo

  // 檢查全社區強制登出
  if (data?.LockAll?.Lock === true) {
    store.commit('triggerForceLogout', true)
    return
  }

  // 檢查特定帳號強制登出（目前僅支援舊制帳號）
  if (account && data?.[account]?.Lock === true) {
    store.commit('triggerForceLogout', true)
    return
  }

  // 未被強制登出
  store.commit('triggerForceLogout', false)
}

/**
 * get() 備援：onValue 初始值逾時或錯誤時改用 get() 拿一次資料
 * 補「RTDB 連線正常、但監聽初始化當下沒拿到初始值」的情境
 * @param {object} logoutRef - RTDB 參照
 * @param {Function} onSettled - 結束（成功或失敗）後的 callback
 */
async function fallbackToGet (logoutRef, onSettled) {
  try {
    const snapshot = await withTimeout(get(logoutRef), GET_TIMEOUT_MS)
    handleDataChange(snapshot)
  } catch (error) {
    // get() 也失敗 → fail-open：放行登入（安全閥）
    // 刻意不 unsubscribe onValue，RTDB 恢復後仍能收到 lock 更新補執行強制登出（eventual consistency）
    console.error('[forceLogout] get() 備援失敗，fail-open 放行登入:', error)
    store.commit('triggerForceLogout', false)
  } finally {
    onSettled && onSettled()
  }
}

/**
 * 清除所有現有的監聽器與計時器
 */
function clearExistingListeners () {
  if (typeof currentUnsubscribeFunction === 'function') {
    currentUnsubscribeFunction()
  }
  currentUnsubscribeFunction = null

  if (currentTimeoutId !== null) {
    clearTimeout(currentTimeoutId)
    currentTimeoutId = null
  }
}

/**
 * 初始化強制登出功能
 */
function forceLogoutUsersByCommunity () {
  // 清除之前的監聽器與狀態
  clearExistingListeners()

  if (!FORCE_LOGOUT_ENABLED) {
    store.commit('triggerForceLogout', false)
    return
  }

  store.commit('triggerForceLogout', null)

  // 初始化 Firebase
  const firebaseApp = getFirebaseApp('smartlife-3d231')
  const database = getDatabase(firebaseApp)

  // 如果已登入，要監聽是否強制登出
  const loginUserInfo = JSON.parse(localStorage.getItem('loginUserInfo') || '{}')
  const { ComId: comId } = loginUserInfo
  if (!comId) return

  // 監聽整個社區的強制登出設定
  const logoutRef = ref(database, `Logout/${comId}`)

  // 是否已取得初始值（onValue 首次回傳 / get 備援 / fail-open 皆視為已解決）
  // 用來避免逾時與 get 備援在已取得初始值後重複觸發
  let initialResolved = false
  const markResolved = () => {
    initialResolved = true
    if (currentTimeoutId !== null) {
      clearTimeout(currentTimeoutId)
      currentTimeoutId = null
    }
  }

  // onValue 逾時：初始值未在時限內到達 → 改用 get() 備援
  currentTimeoutId = setTimeout(() => {
    if (initialResolved) return
    console.warn('[forceLogout] onValue 逾時，改用 get() 備援')
    fallbackToGet(logoutRef, markResolved)
  }, ONVALUE_TIMEOUT_MS)

  const unsubscribeLogout = onValue(
    logoutRef,
    (snapshot) => {
      // 首次回傳取消逾時；後續即時更新（踢人 / sync）也會持續進來
      markResolved()
      handleDataChange(snapshot)
    },
    (error) => {
      // 連線錯誤會直接觸發 error，不會走 timeout，必須在此補備援
      console.error('[forceLogout] onValue 錯誤:', error)
      if (!initialResolved) {
        fallbackToGet(logoutRef, markResolved)
      }
    }
  )
  currentUnsubscribeFunction = unsubscribeLogout
}

export { forceLogoutUsersByCommunity }
